/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { EvalResponseType } from '@/ux-common/src/types/api';
import {
  Match,
  TSequenceData,
  TSequenceDataForConversations,
} from 'types/prompt-sequence-view';

type UseConcurrentSearchOptions = {
  /** If true, eval matches are included in Prev/Next navigation; default false. */
  includeEvalInNavigation?: boolean;
};

/**
 * Search across prompts (inputs + outputs) and trace eval labels.
 * - Returns prompt matches for highlighting & navigation.
 * - Returns eval matches separately (not navigated by default).
 * - Computes per-span, per-message match counts for precise global indexing.
 */
export const useConcurrentSearch = (
  sequenceData: (TSequenceData | TSequenceDataForConversations)[] = [],
  traceEvals: EvalResponseType | null,
  selectedTraceIds: string[] = [],
  options: UseConcurrentSearchOptions = {}
) => {
  const { includeEvalInNavigation = false } = options;

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isPending, startTransition] = useTransition();

  const deferredSearchTerm = useDeferredValue(searchTerm);

  /** Filter data by selected traces before searching. */
  const filteredSequenceData = useMemo(() => {
    if (!selectedTraceIds?.length) return sequenceData;
    return sequenceData.filter((item) =>
      selectedTraceIds.includes(item.trace_id)
    );
  }, [sequenceData, selectedTraceIds]);

  /**
   * Core search:
   * - promptMatches: all matches in prompt text (inputs then outputs).
   * - evalMatches:   all matches found in eval label texts.
   * - spanMessageMatchCounts: Map<span_id, number[]> where number[i] is the count in message i.
   */
  const { promptMatches, evalMatches, spanMessageMatchCounts } = useMemo(() => {
    const empty = {
      promptMatches: [] as Match[],
      evalMatches: [] as Match[],
      spanMessageMatchCounts: new Map<string, number[]>(),
    };

    const term = deferredSearchTerm?.trim();
    if (!term) return empty;

    const makeSafeRegex = (s: string) =>
      new RegExp(s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');

    const promptRegex = makeSafeRegex(term);

    const promptMatchesAcc: Match[] = [];
    const evalMatchesAcc: Match[] = [];
    const countsBySpan = new Map<string, number[]>();

    const bumpCount = (spanId: string, messageIndex: number) => {
      const arr = countsBySpan.get(spanId) ?? [];
      arr[messageIndex] = (arr[messageIndex] ?? 0) + 1;
      countsBySpan.set(spanId, arr);
    };

    /** Collect matches for a single span's prompts (inputs then outputs). */
    const collectForSpan = (
      spanId: string,
      inputs: any[] = [],
      outputsLike: any
    ) => {
      const outputs = Array.isArray(outputsLike) ? outputsLike : [outputsLike];
      const totalMessages = (inputs?.length ?? 0) + (outputs?.length ?? 0);

      // Pre-size so we always have deterministic indices
      if (!countsBySpan.has(spanId)) {
        countsBySpan.set(spanId, new Array(totalMessages).fill(0));
      }

      // inputs first
      inputs?.forEach((prompt, i) => {
        const content =
          typeof prompt === 'string' ? prompt : prompt?.content || '';
        let m: RegExpExecArray | null;
        while ((m = promptRegex.exec(content)) !== null) {
          promptMatchesAcc.push({
            type: 'prompt',
            span_id: spanId,
            promptType: 'input',
            promptIndex: i,
            matchText: m[0],
            startIndex: m.index,
            endIndex: m.index + m[0].length,
          });
          bumpCount(spanId, i);
        }
        promptRegex.lastIndex = 0; // reset for next string
      });

      // then outputs
      outputs?.forEach((prompt, j) => {
        const content =
          typeof prompt === 'string' ? prompt : prompt?.content || '';
        let m: RegExpExecArray | null;
        while ((m = promptRegex.exec(content)) !== null) {
          promptMatchesAcc.push({
            type: 'prompt',
            span_id: spanId,
            promptType: 'output',
            promptIndex: j, // index within outputs
            matchText: m[0],
            startIndex: m.index,
            endIndex: m.index + m[0].length,
          });
          bumpCount(spanId, (inputs?.length ?? 0) + j);
        }
        promptRegex.lastIndex = 0;
      });
    };

    // Walk filtered data and collect prompt matches/counts
    filteredSequenceData.forEach((item) => {
      if ('span_id' in item) {
        collectForSpan(item.span_id, item.inputData, item.outputData);
      } else if ('spans_data' in item) {
        // Conversations view is not used for searching/highlighting,
        // but we still collect in case you later want it.
        item.spans_data?.forEach((span) => {
          collectForSpan(span.span_id, span.inputData, span.outputData);
        });
      }
    });

    // Eval label matches are kept separate (do not alter per-message offsets)
    if (traceEvals?.results?.length) {
      const evalRegex = makeSafeRegex(term);

      traceEvals.results.forEach((res) => {
        // Respect selected trace filter
        if (selectedTraceIds?.length && !selectedTraceIds.includes(res.fact_id))
          return;

        const label = res.eval_result?.label || '';
        if (!label) return;

        const found = Array.from(label.matchAll(evalRegex));
        if (!found.length) return;

        // Find a representative span within the filtered data for this trace
        const parent = filteredSequenceData.find(
          (d) => d.trace_id === res.fact_id
        );
        if (parent && 'span_id' in parent) {
          found.forEach((m) => {
            evalMatchesAcc.push({
              type: 'eval',
              span_id: parent.span_id,
              matchText: m[0],
              // optional: you can add label positions if needed
            });
          });
        }
      });
    }

    return {
      promptMatches: promptMatchesAcc,
      evalMatches: evalMatchesAcc,
      spanMessageMatchCounts: countsBySpan,
    };
  }, [deferredSearchTerm, filteredSequenceData, traceEvals, selectedTraceIds]);

  /** Which set drives Prev/Next navigation & highlighting. */
  const navigableMatches = includeEvalInNavigation
    ? [...promptMatches, ...evalMatches]
    : promptMatches;

  // Controls & effects

  /** Keep index valid when filters or results change. */
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [JSON.stringify([...selectedTraceIds].sort())]);

  useEffect(() => {
    // If the result set size changes (new page, new term, etc.), reset index.
    setCurrentMatchIndex(0);
  }, [navigableMatches.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    startTransition(() => {
      setSearchTerm(value);
      setCurrentMatchIndex(0);
    });
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentMatchIndex(0);
  };

  const onPreviousMatch = () => {
    setCurrentMatchIndex((prev) => Math.max(0, prev - 1));
  };

  const onNextMatch = () => {
    setCurrentMatchIndex((prev) =>
      Math.min(navigableMatches.length - 1, prev + 1)
    );
  };

  return {
    // UI state
    searchInput,
    deferredSearchTerm,
    isPending,

    // Controls
    handleInputChange,
    handleClear,
    onPreviousMatch,
    onNextMatch,
    setCurrentMatchIndex, // exposed for external sync if needed

    // Results
    promptMatches, // matches in prompt text (used for highlighting)
    evalMatches, // matches in eval labels (for badges/side UI)
    navigableMatches, // the list that Prev/Next walks through
    spanMessageMatchCounts, // Map<span_id, number[]> for per-message local offsets
    currentMatchIndex, // 0-based global index into navigableMatches
  };
};
